<template>
  <div class="st">
    <!-- Loading -->
    <template v-if="isLoading">
      <div class="st-loading">
        <div class="st-skel-days">
          <div v-for="i in 5" :key="i" class="vm-skel st-skel-day" />
        </div>
        <div class="st-skel-slots">
          <div v-for="i in 8" :key="i" class="vm-skel st-skel-slot" />
        </div>
      </div>
    </template>

    <template v-else>
      <!-- Com slots disponíveis -->
      <div v-if="slots.length">
        <!-- Seletor de dias (swiper) -->
        <div class="st-header">
          <button
            type="button"
            class="st-arrow st-arrow--prev"
            aria-label="Dias anteriores"
          >
            <BaseIcon icon="chevron-left" size="sm" />
          </button>

          <div class="st-days-track">
            <Swiper :options="swiperOptions">
              <SwiperSlide v-for="(day, index) in slotDays" :key="index">
                <button
                  type="button"
                  class="st-day"
                  :class="{
                    'st-day--active':
                      (index === 0 && !selectedSlotDay) ||
                      day.slot_date === selectedSlotDay
                  }"
                  @click="selectSlotDay(day.slot_date)"
                >
                  <span class="st-day-date">
                    {{ slotDayDate(day.slot_date) }}
                  </span>
                  <span class="st-day-week">
                    {{ slotWeekDay(day.slot_date) }}
                  </span>
                </button>
              </SwiperSlide>
            </Swiper>
          </div>

          <button
            type="button"
            class="st-arrow st-arrow--next"
            aria-label="Próximos dias"
          >
            <BaseIcon icon="chevron-right" size="sm" />
          </button>
        </div>

        <!-- Loading ao trocar de dia -->
        <div v-if="isSelectingSlotDay" class="st-loading st-loading--sm">
          <BaseSpinner size="sm" />
        </div>

        <!-- Grade de horários -->
        <div v-else class="st-slots">
          <button
            v-for="slot in listAvailableSlots(
              availableSlots[selectedSlotDay] ||
                availableSlots[Object.keys(availableSlots)[0]]
            )"
            :key="slot.slot_id + '-' + slot.hour"
            type="button"
            class="st-slot"
            :class="{ 'st-slot--active': selectedSlot.hour === slot.hour }"
            @click="selectSlot(slot)"
          >
            {{ slot.hour }}
          </button>
        </div>

        <!-- Footer: ver mais -->
        <div v-if="!isSelectingSlotDay" class="st-footer">
          <BaseButton
            variant="link"
            @click="showMore = !showMore"
            class="st-more-btn"
          >
            {{ showMore ? "Ver menos horários" : "Ver todos os horários" }}
            <BaseIcon
              :icon="showMore ? 'chevron-up' : 'chevron-down'"
              size="xs"
            />
          </BaseButton>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-else class="st-empty">
        <div class="st-empty-ill">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <p class="st-empty-text">Verificar disponibilidade</p>
      </div>

      <div
        v-if="showDoctorInConsultationMessage"
        class="schedule-table-empty mt-2 mb-2"
      >
        <p class="schedule-table-empty-title mb-0">
          {{ doctorInConsultationMessage }}
        </p>
      </div>

      <div
        v-if="entity.available_for_immediate_consultation"
        class="schedule-table-empty mt-2 mb-2"
      >
        <BaseButton size="xl" @click="goToPaymentScreen">
          Ser atendido agora
        </BaseButton>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { formatDate, formatWeekDay } from "../../utils/masks/formatSlotDate";
import {
  canStartImmediateConsultation,
  DOCTOR_IN_CONSULTATION_MESSAGE,
  isDoctorInConsultation,
  showImmediateConsultationUnavailableAlert
} from "../../utils/appointments/immediateConsultation";

export default {
  name: "ScheduleTable",
  props: {
    entity: {
      type: Object,
      default: () => {}
    },
    requestNewSlots: {
      type: Function,
      required: true
    },
    unitId: {
      type: String,
      default: ""
    },
    onlySelectSlot: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      error: null,
      isLoading: false,
      isSelectingSlotDay: false,
      showMore: false,
      slotDaysCurrentPage: 0,
      slots: [],
      selectedSlotDay: "",
      selectedSlot: {}
    };
  },
  mounted() {
    if (this.entity.id) {
      this.handleFetchDoctorsAvailables();
    }
  },
  methods: {
    ...mapActions("user", ["showUser"]),
    ...mapActions("doctor", ["fetchDoctorsAvailables", "sendSMStoDoctor"]),
    ...mapActions("appointments", ["holdScheduleAppointment"]),
    ...mapActions("slot", ["setSlotsRequested", "setSlotsRequestedDoctorId"]),

    async handleFetchDoctorsAvailables() {
      try {
        this.isLoading = true;
        const response = await this.fetchDoctorsAvailables({
          id: this.entity.id,
          query: this.unitId ? `unit_id=${this.unitId}` : ""
        });
        this.slots = response.data.filter(availableSlot => availableSlot.id);
      } catch (err) {
        this.error = err;
      } finally {
        this.isLoading = false;
      }
    },

    slotWeekDay(value) {
      return formatWeekDay(value);
    },
    slotDayDate(value) {
      return formatDate(value);
    },
    availableSlotHour(hour) {
      return !this.showMore ? hour.slice(0, 4) : hour;
    },
    selectSlotDay(date) {
      this.isSelectingSlotDay = true;
      this.selectedSlotDay = date;
      setTimeout(() => {
        this.isSelectingSlotDay = false;
      }, 400);
    },
    listAvailableSlots(slots) {
      return this.showMore ? slots : slots.slice(0, 15);
    },
    scheduleSlot(hour, date, id) {
      this.holdScheduleAppointment({
        scheduled_at: `${date} ${hour}:00`,
        scheduled_until: `${date} 00:00:00`,
        doctor: this.entity,
        slot_date: date,
        slot_hour: hour,
        slot_id: id
      });
      if (!this.loggedIn) {
        this.$bvModal.show("signinModal");
      } else {
        this.$router.push({ name: "Schedule" });
      }
    },
    goToPaymentScreen() {
      if (!canStartImmediateConsultation(this.entity)) {
        showImmediateConsultationUnavailableAlert();
        return;
      }

      const current = new Date();
      const date = current.toISOString().split("T")[0];
      const time = current.toLocaleTimeString();

      this.holdScheduleAppointment({
        scheduled_at: this.from_date || `${date} ${time}`,
        scheduled_until: this.until_date || `${date} ${time}`,
        doctor: this.entity,
        slot_date: this.from_date || `${date} ${time}`,
        slot_hour: time,
        slot_id: null
      });

      this.$router.push({ name: "Schedule", query: { immediate: true } });
    },
    selectSlot(slot) {
      if (this.onlySelectSlot) {
        this.$emit("on-select-slot", slot);
        this.selectedSlot = slot;
      } else {
        this.scheduleSlot(slot.hour, slot.slot_date, slot.slot_id);
      }
    }
  },
  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["getCurrentUser"]),
    ...mapGetters("slot", ["slotsRequested", "units"]),

    from_date() {
      return new URLSearchParams(window.location.search).get("from_date");
    },
    until_date() {
      return new URLSearchParams(window.location.search).get("until_date");
    },
    availableSlots() {
      let slots = this.slots.reduce((prevValue, currentValue) => {
        prevValue[currentValue.slot_date] = [
          ...(prevValue[currentValue.slot_date] || []),
          ...currentValue.slots.map(slot => ({
            slot_id: currentValue.id,
            slot_date: currentValue.slot_date,
            hour: slot
          }))
        ];
        return prevValue;
      }, {});
      return slots;
    },
    slotDays() {
      let days = [];
      let slots = this.availableSlots;
      Object.keys(slots).filter(day => {
        if (slots[day]) {
          days.push({
            slots: this.availableSlotHour(slots[day]),
            slot_date: day
          });
        }
      });
      return days;
    },
    swiperOptions() {
      return {
        slidesPerView: 2,
        spaceBetween: 8,
        centeredSlides: false,
        loop: false,
        navigation: {
          nextEl: ".st-arrow--next",
          prevEl: ".st-arrow--prev"
        },
        breakpoints: {
          1200: { slidesPerView: 5, spaceBetween: 8 },
          992: { slidesPerView: 5, spaceBetween: 8 },
          768: { slidesPerView: 4, spaceBetween: 8 },
          360: { slidesPerView: 3, spaceBetween: 8 }
        }
      };
    },
    slotDaysMaxPage() {
      return Math.ceil(Object.keys(this.slotDays).length / 4) - 1;
    },
    showDoctorInConsultationMessage() {
      return isDoctorInConsultation(this.entity);
    },
    doctorInConsultationMessage() {
      return DOCTOR_IN_CONSULTATION_MESSAGE;
    }
  },

  watch: {
    unitId(newValue) {
      if (newValue) {
        this.handleFetchDoctorsAvailables();
      }
    }
  }
};
</script>

<style lang="scss">
/* Global: swiper container dentro do ScheduleTable */
.st .swiper-container {
  width: 100%;
}
</style>

<style lang="scss" scoped>
.st {
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
}

// Loading skeleton
.st-loading {
  padding: var(--space-4, 16px) 0;

  &--sm {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.st-skel-days {
  display: flex;
  gap: var(--space-2, 8px);
  margin-bottom: var(--space-3, 12px);
}

.st-skel-day {
  flex: 1;
  height: 52px;
  border-radius: var(--radius-md, 8px);
}

.st-skel-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: var(--space-2, 8px);
}

.st-skel-slot {
  height: 38px;
  border-radius: var(--radius-sm, 6px);
}

// Header: setas + swiper de dias
.st-header {
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  gap: var(--space-2, 8px);
  align-items: center;
  margin-bottom: var(--space-3, 12px);
}

.st-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1.5px solid var(--color-gray-200, #e2e8f0);
  border-radius: var(--radius-md, 8px);
  background: #fff;
  cursor: pointer;
  color: var(--color-gray-600, #475569);
  transition: border-color 0.15s, background 0.15s;
  padding: 0;

  &:hover {
    border-color: var(--color-primary, #1a6cf7);
    color: var(--color-primary, #1a6cf7);
    background: var(--color-primary-light, #ebf2ff);
  }

  &.swiper-button-disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.st-days-track {
  overflow: hidden;
  min-width: 0;
}

// Botão de dia
.st-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: var(--space-2, 8px) var(--space-1, 4px);
  border: 1.5px solid var(--color-gray-200, #e2e8f0);
  border-radius: var(--radius-md, 8px);
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--color-primary, #1a6cf7);
    background: var(--color-primary-light, #ebf2ff);
  }

  &--active {
    border-color: var(--color-primary, #1a6cf7);
    background: var(--color-primary, #1a6cf7);

    .st-day-date,
    .st-day-week {
      color: #fff;
    }
  }
}

.st-day-date {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--fw-bold, 700);
  color: var(--color-gray-800, #1e293b);
  white-space: nowrap;
  font-family: var(--font-num, "Inter", sans-serif);
  line-height: 1.3;
}

.st-day-week {
  font-size: 10px;
  font-weight: var(--fw-semibold, 600);
  color: var(--color-gray-400, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

// Grade de horários
.st-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: var(--space-2, 8px);
  margin-bottom: var(--space-2, 8px);
}

.st-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2, 8px);
  border: 1.5px solid var(--color-gray-200, #e2e8f0);
  border-radius: var(--radius-sm, 6px);
  background: var(--color-gray-50, #f8fafc);
  font-family: var(--font-num, "Inter", sans-serif);
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-gray-700, #334155);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;

  &:hover {
    border-color: var(--color-accent, #d81a67);
    background: var(--color-accent-light, #fff0f7);
    color: var(--color-accent, #d81a67);
  }

  &--active {
    border-color: var(--color-accent, #d81a67);
    background: var(--color-accent, #d81a67);
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Footer
.st-footer {
  margin-top: var(--space-3, 12px);
  text-align: center;
}

.st-more-btn {
  font-size: var(--font-size-xs, 0.75rem) !important;
  color: var(--color-primary, #1a6cf7) !important;
  font-weight: var(--fw-bold, 700);
}

// Estado vazio
.st-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-8, 32px) var(--space-4, 16px);
  text-align: center;
}

.st-empty-ill {
  width: 52px;
  height: 52px;
  border-radius: var(--radius-lg, 12px);
  background: var(--color-gray-100, #f1f5f9);
  color: var(--color-gray-400, #94a3b8);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-3, 12px);
}

.st-empty-text {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-gray-500, #64748b);
  margin: 0;
}

// CTA: atendimento imediato
.st-online-cta {
  margin-top: var(--space-3, 12px);
}

.st-online-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2, 8px);
  width: 100%;
  padding: var(--space-3, 12px) var(--space-4, 16px);
  border: none;
  border-radius: var(--radius-md, 8px);
  background: var(--color-accent, #d81a67);
  color: #fff;
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--fw-bold, 700);
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--color-accent-dark, #b01254);
  }
}
</style>
